/**
 * Fleet status (operator fleet Overview, /fleet): per customer, the trust stack state, the collector state and the
 * last run, so an operator sees who needs a push. Pure functions over the customer record the operator already gets
 * from GET /customers: no live cross-account call is made to compute any of it.
 *
 * Honesty rules:
 *   - a trust stack nobody has checked yet is Unknown, never "current" (trustStackOutdated returns null for it);
 *   - a collector whose deployed code key was never recorded (no push since the field existed, no backfill) is
 *     Unknown, never "current" and never "behind";
 *   - the reasons are short categories (lib/code-push-outcome.mjs), never AWS text.
 */
import { TRUST_STACK_VERSION, trustStackOutdated } from './trust-stack.mjs';

export const TRUST_STATE = Object.freeze({ CURRENT: 'current', OUTDATED: 'outdated', UNKNOWN: 'unknown', NONE: 'none' });
export const COLLECTOR_STATE = Object.freeze({
  CURRENT: 'current', BEHIND: 'behind', FAILED: 'failed', IN_PROGRESS: 'in-progress', UNKNOWN: 'unknown', NONE: 'none',
});
export const RUN_STATE = Object.freeze({ HEALTHY: 'healthy', PARTIAL: 'partial', TIMED_OUT: 'timed-out', FAILED: 'failed', NONE: 'none' });

/** A code push failure category (lib/code-push-outcome.mjs) in words. */
export const PUSH_FAILURE_LABELS = Object.freeze({
  'trust-permissions': 'access role permissions',
  'rolled-back': 'rolled back',
  'in-progress': 'still in progress',
  'stack-failed': 'stack update failed',
});

/** The statuses the fleet push reaches (routes/fleet-push-code.mjs): only these can be pushed to. */
const PUSHABLE_STATUSES = new Set(['deployed', 'active']);

/** Would the fleet push reach this customer? The same rule as routes/fleet-push-code.mjs. */
export function isPushable(customer) {
  return PUSHABLE_STATUSES.has(customer?.status) && !!customer?.stackName && !!customer?.trustRoleArn;
}

/** A push still "in progress" after this long is not trusted to still be running. */
const PUSH_STALE_MS = 60 * 60 * 1000;

/** The trust stack: { state, label, version, checkedAt }. */
export function trustState(customer) {
  const checkedAt = typeof customer?.trustStackCheckedAt === 'string' ? customer.trustStackCheckedAt : null;
  if (!customer?.trustRoleArn) return { state: TRUST_STATE.NONE, label: 'No trust stack', version: null, checkedAt };
  const outdated = trustStackOutdated(customer);
  const version = Number.isInteger(customer.trustStackVersion) ? customer.trustStackVersion : null;
  if (outdated === null || version === null) {
    return { state: TRUST_STATE.UNKNOWN, label: 'Unknown (not checked yet)', version: null, checkedAt };
  }
  if (outdated) {
    return { state: TRUST_STATE.OUTDATED, label: `${version} of ${TRUST_STACK_VERSION}, update needed`, version, checkedAt };
  }
  return { state: TRUST_STATE.CURRENT, label: `${version} of ${TRUST_STACK_VERSION}`, version, checkedAt };
}

/**
 * The collector: the code key recorded at the last push (lastCodePushKey) against the latest key (COLLECTOR_CODE_KEY).
 * @returns {{ state: string, label: string, pushedAt: string|null, deployedKey: string|null }}
 */
export function collectorState(customer, latestKey, now = Date.now()) {
  const pushedAt = typeof customer?.lastCodePushAt === 'string' ? customer.lastCodePushAt : null;
  const deployedKey = typeof customer?.lastCodePushKey === 'string' && customer.lastCodePushKey ? customer.lastCodePushKey : null;
  const base = { pushedAt, deployedKey };
  if (!customer?.stackName) return { ...base, state: COLLECTOR_STATE.NONE, label: 'Not deployed' };
  if (customer.lastCodePushStatus === 'failed') {
    const reason = PUSH_FAILURE_LABELS[customer.lastCodePushFailureReason] ?? 'unknown reason';
    return { ...base, state: COLLECTOR_STATE.FAILED, label: `Push failed: ${reason}` };
  }
  if (customer.lastCodePushStatus === 'in-progress') {
    // A push that has been "in progress" for over an hour never reported back: its outcome is not known.
    const startedMs = pushedAt ? Date.parse(pushedAt) : NaN;
    if (Number.isFinite(startedMs) && now - startedMs > PUSH_STALE_MS) {
      return { ...base, state: COLLECTOR_STATE.UNKNOWN, label: 'Unknown (push outcome not confirmed)' };
    }
    return { ...base, state: COLLECTOR_STATE.IN_PROGRESS, label: 'Push in progress' };
  }
  if (!deployedKey || typeof latestKey !== 'string' || !latestKey) return { ...base, state: COLLECTOR_STATE.UNKNOWN, label: 'Unknown' };
  if (deployedKey === latestKey) return { ...base, state: COLLECTOR_STATE.CURRENT, label: 'Current' };
  return { ...base, state: COLLECTOR_STATE.BEHIND, label: 'Behind' };
}

/** The last run: { state, label, at, collectorVersion }. Same words as the portal's run status labels. */
export function runState(customer) {
  const at = typeof customer?.lastRunAt === 'string' ? customer.lastRunAt : null;
  const collectorVersion = typeof customer?.lastRunCollectorVersion === 'string' ? customer.lastRunCollectorVersion : null;
  const code = customer?.lastRunStatusCode;
  if (!at || code == null) return { state: RUN_STATE.NONE, label: 'No run yet', at, collectorVersion };
  if (code === 200) return { state: RUN_STATE.HEALTHY, label: 'Healthy', at, collectorVersion };
  if (code === 207) return { state: RUN_STATE.PARTIAL, label: 'Partial', at, collectorVersion };
  if (code === 206) return { state: RUN_STATE.TIMED_OUT, label: 'Timed out', at, collectorVersion };
  return { state: RUN_STATE.FAILED, label: 'Failed', at, collectorVersion };
}

/** Why a customer needs attention, as short words (empty: it does not). A decommissioned customer never does. */
export function attentionReasons(customer, latestKey) {
  if (customer?.status === 'decommissioned' || customer?.decommissionedAt) return [];
  const reasons = [];
  if (trustState(customer).state === TRUST_STATE.OUTDATED) reasons.push('trust stack outdated');
  const collector = collectorState(customer, latestKey).state;
  if (collector === COLLECTOR_STATE.BEHIND) reasons.push('collector behind');
  if (collector === COLLECTOR_STATE.FAILED) reasons.push('push failed');
  if (runState(customer).state === RUN_STATE.FAILED) reasons.push('last run failed');
  return reasons;
}

/** One row per customer, in the order given. */
export function fleetStatusRows(customers, latestKey) {
  return (customers ?? []).map((customer) => {
    const collector = collectorState(customer, latestKey);
    return {
      customer,
      trust: trustState(customer),
      collector,
      run: runState(customer),
      attention: attentionReasons(customer, latestKey),
      // Only a customer the fleet push reaches is offered in "Push to the N behind".
      pushBehind: collector.state === COLLECTOR_STATE.BEHIND && isPushable(customer),
    };
  });
}

/** The customer ids "Push to the N behind" sends to the fleet push. */
export function behindCustomerIds(customers, latestKey) {
  return fleetStatusRows(customers, latestKey).filter((r) => r.pushBehind).map((r) => r.customer.customerId);
}
